import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { DynamicTemplateConfigService } from '../dynamic-template-config.service';
import { DynamicTemplateConfig } from '../models/dynamic-template-config.interface';
import { InvoiceData } from '../models/invoice-data.interface';
import { A5LandscapeTemplate } from './a5-landscape-template';
import { BasePdfTemplate } from './base-template';
import { DynamicSectionBuilder } from './builders/dynamic-section-builder';
import { buildInvoiceNameTitle, invoiceNameTitleHeight } from './helpers/invoice-name.helper';

/**
 * Dynamic PDF Template - Handles all template_id >= 8
 * Uses JSON configuration to generate PDFs dynamically
 */
export class DynamicPdfTemplate extends BasePdfTemplate {
  override templateName = 'dynamic-template';
  override templateVersion = '2.0.0';
  override description = 'Dynamic JSON-driven PDF template for template_id >= 8';
  override pageSize: 'A3' | 'A4' | 'A5' | 'LETTER' | 'THERMAL' = 'A4'; // Default, will be overridden by config

  private config: DynamicTemplateConfig | null = null;
  private templateId: number = 8; // Default template ID
  private configService: DynamicTemplateConfigService | null = null;
  font: string = 'NotoSansTamil';

  constructor(configService?: DynamicTemplateConfigService) {
    super();
    this.configService = configService || null;
  }

  setConfigService(configService: DynamicTemplateConfigService): void {
    this.configService = configService;
  }

  /**
   * Set the template ID for this instance
   * This should be called before generatePdfDefinition
   */
  setTemplateId(templateId: number): void {
    if (templateId < 8) {
      console.error(`Invalid template ID ${templateId} for dynamic template. Must be >= 8`);
      return;
    }
    this.templateId = templateId;
  }

  /**
   * Load configuration for the current template ID
   */
  private async loadConfig(): Promise<DynamicTemplateConfig> {
    if (this.config) {
      return this.config;
    }

    if (!this.configService) {
      throw new Error('DynamicTemplateConfigService not set. Call setConfigService() before generating PDF.');
    }

    try {
      const config = await this.configService.getTemplateConfig(this.templateId).toPromise();

      if (!config) {
        throw new Error(`Failed to load configuration for template_id ${this.templateId}`);
      }

      this.config = config;

      // Update page size from config
      this.pageSize = config.pageSettings.pageSize;

      console.log(`[DynamicPdfTemplate] Loaded configuration for template_id ${this.templateId}:`, config.metadata.templateName);

      return config;
    } catch (error) {
      console.error(`[DynamicPdfTemplate] Error loading template configuration:`, error);
      throw new Error(`Failed to load template configuration: ${error}`);
    }
  }

  /**
 * A5 Portrait top content height — uses FIXED table heights from the builder.
 * party section: ROW_HEIGHTS = [18,18,16,16,16] → 84pt total
 * invoice section: ROW_HEIGHT = 18, always 4 rows → 72pt total
 * They are side-by-side so height = max(84, 72) = 84pt
 * Business section is estimated normally.
 */
  private estimateA5PortraitTopContentHeight(config: DynamicTemplateConfig): number {
    const baseFontSize = config.themeSettings.baseFontSize || 8;
    const bc = config.businessDetails;
    const spacing = config.layoutSettings.sectionSpacing || 5;

    // Business section height
    let businessH = 0;
    const businessVisible = this.isSectionVisible(config, 'business') ||
      this.isSectionVisible(config, 'header');
    if (businessVisible) {
      // A5 Portrait business: 3-column layout (invoice title | centered details | logo)
      // Typically 80pt fixed minimum (enforced by heights: () => 80 in builder)
      businessH = 80;
    }

    // Party + Invoice: fixed heights enforced by builder
    const partyVisible = this.isSectionVisible(config, 'party');
    const invoiceVisible = this.isSectionVisible(config, 'invoiceDetails');
    let partyInvoiceH = 0;
    if (partyVisible || invoiceVisible) {
      // party: sum([18,18,16,16,16]) = 84pt
      // invoice: 18 * 4 = 72pt  
      // outer wrapper has heights: () => 47 minimum but actual = max of content
      partyInvoiceH = 84; // max(party=84, invoice=72)
    }

    return businessH + partyInvoiceH + spacing;
  }
  /**
   * Estimate A5 Portrait footer height for absolutePosition calculation.
   * Sections: TOTAL row + payment row + amount-in-words + 3-col footer
   */
  private estimateA5PortraitFooterHeight(
    config: DynamicTemplateConfig,
    data: InvoiceData,
  ): number {
    let h = 0;
    const totals: any = config.totalsSection || {};
    const footer: any = config.footer || {};
    const baseFontSize = config.themeSettings.baseFontSize || 8;
    const fontSize = baseFontSize - 1;

    // TOTAL summary row
    h += (config.itemTable?.bodyStyle?.rowHeight || 18) + 6;

    // Payment + Tax block (side-by-side 2-col table)
    let leftH = 0;
    if (totals.showReceivedAmount) leftH += fontSize * 1.4 + 4;
    if (totals.showPreviousBalance) leftH += fontSize * 1.4 + 4;
    if (totals.showCurrentBalance) leftH += fontSize * 1.4 + 4;

    let rightH = 0;
    if (totals.showTaxBreakup !== false) {
      if (totals.showCGST !== false && (data as any).cgst) rightH += fontSize * 1.4 + 3;
      if (totals.showSGST !== false && (data as any).sgst) rightH += fontSize * 1.4 + 3;
      if (totals.showIGST !== false && (data as any).igst) rightH += fontSize * 1.4 + 3;
    }
    if (totals.showGrandTotal !== false) rightH += (fontSize + 1) * 1.4 + 5;

    const paymentBlockH = Math.max(leftH, rightH);
    if (paymentBlockH > 0) h += paymentBlockH + 8; // FIX 2: h += not h =

    // Amount in words
    if (totals.showAmountInWords) h += (fontSize - 1) * 1.4 + 10;

    // 3-column footer: Bank | Terms | Signature
    const hasFooter3Col =
      footer.showBankDetails ||
      footer.showTermsAndConditions ||
      footer.showSignature;

    if (hasFooter3Col) {
      let maxColH = 0;

      if (footer.showBankDetails) {
        let bankH = (fontSize - 1) * 1.4 + 4; // heading
        const bd = (data as any).bankDetails;
        const lineCount = bd
          ? [bd.bankName, bd.ifscCode, bd.accountNumber, bd.upiId].filter(Boolean).length
          : 4;
        bankH += lineCount * ((fontSize - 1) * 1.4 + 2);
        const qrEnabled = (footer as any).showQrCode ?? footer.showQRCode ?? false;
        if (qrEnabled && (data as any).qrcode) bankH += 41;
        bankH += 8;
        maxColH = Math.max(maxColH, bankH);
      }

      if (footer.showTermsAndConditions) {
        const termsText: string =
          footer.termsText || (data as any).termsAndConditions || (data as any).terms || '';
        const pts = termsText.split('\n').filter((p: string) => p.trim()).length;
        let termsH = (fontSize - 1) * 1.4 + 4; // heading
        termsH += Math.max(1, pts) * ((fontSize - 1) * 1.4 + 2);
        termsH += 8;
        maxColH = Math.max(maxColH, termsH);
      }

      if (footer.showSignature) {
        const signatureData =
          (data as any).signature ||
          (data.business as any)?.signature ||
          footer.signature;
        let sigH = signatureData ? 41 : 22;
        sigH += (fontSize - 1) * 1.4 + 4; // label
        if (footer.showForBusinessName) sigH += (fontSize - 1) * 1.4 + 4;
        sigH += 8;
        maxColH = Math.max(maxColH, sigH);
      }

      // FIX 2: h += maxColH (not h = Math.max(h, 60) which discarded everything)
      h += maxColH + 4;
    }

    return Math.max(h, 80); // safety floor
  }
  /**
   * Generate PDF definition based on JSON configuration
   * Note: Configuration should be preloaded using preloadTemplateConfig() before calling this method
   */
  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    try {
      if (!this.configService) {
        throw new Error('DynamicTemplateConfigService not set. Call setConfigService() before generating PDF.');
      }

      let config: DynamicTemplateConfig | undefined = undefined;

      this.configService.getTemplateConfig(this.templateId).subscribe({
        next: (c) => {
          config = c;
        },
        error: (error) => {
          console.error('[DynamicPdfTemplate] Error loading config:', error);
        }
      });

      if (!config) {
        throw new Error('Failed to load template configuration. Please ensure preloadTemplateConfig() is called before generating PDF.');
      }

      const templateConfig: DynamicTemplateConfig = config;

      // ═══ A5 LANDSCAPE: Delegate to standalone A5LandscapeTemplate ═══
      if (templateConfig.pageSettings.pageSize === 'A5' && templateConfig.pageSettings.orientation === 'landscape') {
        return new A5LandscapeTemplate(templateConfig).generatePdfDefinition(data);
      }

      this.config = templateConfig;
      this.pageSize = templateConfig.pageSettings.pageSize;
      this.font = templateConfig.pageSettings.font || 'NotoSansTamil';

      // ═══ A5 PORTRAIT: Force party + invoiceDetails into same row ═══
      if (templateConfig.pageSettings.pageSize === 'A5' &&
        templateConfig.pageSettings.orientation !== 'landscape') {

        const rows = templateConfig.layoutSettings.rows;
        const partyRowIdx = rows.findIndex(r =>
          r.sections.includes('party') && !r.sections.includes('invoiceDetails'));
        const invoiceRowIdx = rows.findIndex(r =>
          r.sections.includes('invoiceDetails') && !r.sections.includes('party'));

        if (partyRowIdx !== -1 && invoiceRowIdx !== -1) {
          // Merge both into a single row, remove the other
          rows[partyRowIdx] = {
            ...rows[partyRowIdx],
            sections: ['party', 'invoiceDetails']
          };
          rows.splice(invoiceRowIdx, 1); // remove the now-duplicate row
        }
      }

      const builder = new DynamicSectionBuilder(templateConfig, data);

      const content: any[] = [];

      /* if (templateConfig.invoiceDetails.showTitle) {
        content.push({
          text: templateConfig.invoiceDetails.title,
          fontSize: templateConfig.invoiceDetails.titleFontSize,
          bold: true,
          alignment: templateConfig.invoiceDetails.titleAlignment,
          margin: [0, 0, 0, 10],
          color: templateConfig.themeSettings.primaryColor
        });
      } */

      const itemsCount = data.items.length;
      console.log(data, 'data of time')
      const A4_HEIGHT = 841.89;
      const margins = templateConfig.pageSettings.margins;
      const isA4 = templateConfig.pageSettings.pageSize === 'A4';
      const contentWidth = 595.28 - margins.left - margins.right;
      const pageContentArea = A4_HEIGHT - margins.top - margins.bottom;

      // Dynamic fit check: Determine if everything fits on one page
      const estTopH = this.estimateTopContentHeight(templateConfig) + invoiceNameTitleHeight(templateConfig);
      const estFooterH = this.estimateA4FooterHeight(templateConfig, data, true);
      const estItemsH = this.estimateA4ItemsHeight(templateConfig, data.items);
      const totalEstimatedH = estTopH + estFooterH + estItemsH + 10; // 10pt safety buffer

      // Strictly use single-page logic for up to 12 items.
      // We will scale down to fit if necessary.
      const useMultiPage = itemsCount > 10;
      const fitsOnOnePage = totalEstimatedH <= pageContentArea;

      if (isA4 && useMultiPage) {
        // PAGINATION LOGIC for A4 with multi-page content
        const limit = 10;
        const chunks: any[][] = [];
        for (let i = 0; i < data.items.length; i += limit) {
          chunks.push(data.items.slice(i, i + limit));
        }

        // Footer section IDs for last-page sticky treatment
        const MP_FOOTER_IDS = ['totals', 'hsnBreakup', 'amountInWords', 'termsAndConditions', 'footer'];

        // Pre-compute fixed page geometry once (same for every page/chunk)
        const A4_HEIGHT = 841.89; // standard A4 height in pdfmake points
        const margins = templateConfig.pageSettings.margins;
        const contentWidth = 595.28 - margins.left - margins.right; // standard A4 width in pdfmake points

        // Compute footer height with potential font scaling for multi-page
        let estimatedFooterHeight = this.estimateA4FooterHeight(templateConfig, data, true);
        let mpFooterFontScale = 1.0;

        // For multi-page, last page has no header sections, so all content area is for items + footer
        const mpTotalPageContent = A4_HEIGHT - margins.top - margins.bottom;
        // Check if last chunk items would fit alongside the footer
        const lastChunkSize = data.items.length % limit || limit;
        const mpRowPadding = 8;
        const mpFontSize = templateConfig.itemTable?.bodyStyle?.fontSize || 8;
        const mpBaseRowH = templateConfig.itemTable?.bodyStyle?.rowHeight || 16;
        const mpActualRowH = Math.max(mpBaseRowH, (mpFontSize * 1.2) + mpRowPadding);
        const mpVisibleCols = templateConfig.itemTable?.columns?.filter((c: any) => c.show) || [];
        const mpIsDiscSplit = mpVisibleCols.some((c: any) => c.key === 'discount' || c.key.includes('Disc')) &&
          mpVisibleCols.some((c: any) => c.key === 'discountPercent' || c.key === 'discount%');
        const mpHdrRows = mpIsDiscSplit ? 2 : 1;
        const mpHdrEst = mpHdrRows * mpActualRowH;
        const mpMinItemsHeight = lastChunkSize * mpActualRowH + mpHdrEst;
        const mpAvailableForFooter = mpTotalPageContent - mpMinItemsHeight;

        if (estimatedFooterHeight > mpAvailableForFooter && mpAvailableForFooter > 0) {
          mpFooterFontScale = Math.max(0.65, mpAvailableForFooter / estimatedFooterHeight);
          estimatedFooterHeight = Math.round(estimatedFooterHeight * mpFooterFontScale);
        }

        const footerY = Math.max(margins.top + 50, A4_HEIGHT - margins.bottom - estimatedFooterHeight);

        for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
          const isLastPage = chunkIndex === chunks.length - 1;
          const currentItems = chunks[chunkIndex];

          // On the last page of multi-page, no header sections appear above the items table,
          // so the items table starts directly at margins.top. Compute filler height to
          // stretch the table bottom edge to the footer's top border.
          let mpItemsFillerHeight = 0;
          if (isLastPage) {
            const visibleColsMp = templateConfig.itemTable?.columns?.filter((c: any) => c.show) || [];
            const colCountMp = visibleColsMp.length || 6;
            const cellPaddingMp = colCountMp >= 14 ? 1 : colCountMp >= 12 ? 2 : colCountMp >= 10 ? 2 : colCountMp >= 8 ? 3 : 4;
            const rowPaddingMp = cellPaddingMp * 2;
            const fontSizeMp = templateConfig.itemTable?.bodyStyle?.fontSize || 8;
            const baseRowHMp = templateConfig.itemTable?.bodyStyle?.rowHeight || 16;
            const actualRowHMp = Math.max(baseRowHMp, (fontSizeMp * 1.2) + rowPaddingMp);

            const isDiscSplitMp = visibleColsMp.some((c: any) => c.key === 'discount' || c.key.includes('Disc')) &&
              visibleColsMp.some((c: any) => c.key === 'discountPercent' || c.key === 'discount%');
            const hdrRowsMp = isDiscSplitMp ? 2 : 1;
            const hdrEstMp = hdrRowsMp * actualRowHMp;

            // 8pt safety buffer: prevents items table from overflowing past footerY
            // due to small row-height estimation inaccuracies on the last page.
            const availForItemsMp = footerY - margins.top - hdrEstMp - 8;
            const rawMpFiller = availForItemsMp - currentItems.length * actualRowHMp;
            // Hard cap: items table must never exceed available page content area
            const mpPageCap = mpTotalPageContent - hdrEstMp - currentItems.length * actualRowHMp - 8;
            mpItemsFillerHeight = Math.max(0, Math.round(Math.min(rawMpFiller, mpPageCap)));
          }
          const lastPageFooterSections: any[] = [];
          const processedMpFooter = new Set<string>();

          for (const row of templateConfig.layoutSettings.rows) {
            // ── FILTERING CONDITIONS: UNCHANGED ─────────────────────────────
            const filteredSections = row.sections.filter(sid => {
              if (sid === 'items') return true;

              // Business, header, invoiceDetails, party only on first page
              if (['business', 'header', 'invoiceDetails', 'party'].includes(sid)) {
                return chunkIndex === 0;
              }

              // Footers only on last page
              if (!isLastPage && ['totals', 'hsnBreakup', 'amountInWords', 'termsAndConditions', 'footer'].includes(sid)) {
                return false;
              }

              return true;
            });
            // ── END UNCHANGED CONDITIONS ─────────────────────────────────────

            if (filteredSections.length === 0) continue;

            if (isLastPage) {
              // Apply footer font scale for multi-page
              builder.setFooterFontScale(mpFooterFontScale);

              // Top sections (items etc.) render normally; footer sections go to absolutePosition block.
              // For 'items' on last page: render WITHOUT summary row — it goes into the footer block.
              const topSids = filteredSections.filter(sid => !MP_FOOTER_IDS.includes(sid));
              const footerSids = filteredSections.filter(sid => MP_FOOTER_IDS.includes(sid));

              if (topSids.length > 0) {
                const builtSections = topSids.map(sid => {
                  if (sid === 'items') {
                    // Render items table WITHOUT the T.Qty/Disc/Total summary row.
                    // Pass mpItemsFillerHeight so the table's bottom edge reaches the footer border.
                    return builder.buildItemsTable(currentItems, false, !isLastPage, true, mpItemsFillerHeight); // hideBottomBorder: summary row is separate
                  }
                  return this.buildPaginatedSection(sid, builder, templateConfig, currentItems, isLastPage);
                }).filter(s => s !== null);

                if (builtSections.length === 1) {
                  content.push(builtSections[0]);
                } else if (builtSections.length > 1) {
                  content.push({ columns: builtSections, columnGap: templateConfig.layoutSettings.sectionSpacing });
                }
              }

              // Collect footer sections (deduplicated across rows)
              for (const sid of footerSids) {
                if (!processedMpFooter.has(sid)) {
                  processedMpFooter.add(sid);
                  const section = this.buildSection(sid, builder, templateConfig);
                  if (section) lastPageFooterSections.push(section);
                }
              }
            } else {
              const builtRow = this.buildPaginatedRow(filteredSections, builder, templateConfig, currentItems, isLastPage);
              if (builtRow) content.push(builtRow);
            }
          }

          // Place last-page footer at absolutePosition (sticky to page bottom).
          // Prepend the T.Qty/Disc/Total summary row so it leads the footer block.
          if (isLastPage && lastPageFooterSections.length > 0) {
            const summaryRow = builder.buildItemsSummaryRow();
            const footerContent = summaryRow ? [summaryRow, ...lastPageFooterSections] : lastPageFooterSections;

            // footerY and contentWidth are pre-computed above the chunk loop
            content.push({
              /* Multi-page last-page sticky footer: summary row + totals + HSN + terms + signatures */
              table: {
                widths: [contentWidth],
                body: [[{ stack: footerContent, margin: [0, 0, 0, 0], fillColor: '#FFFFFF' }]]
              },
              layout: 'noBorders',
              absolutePosition: { x: margins.left, y: footerY }
            });
          }

          if (!isLastPage) {
            content.push({ text: '', pageBreak: 'after' });
          }
        }
      } else if (isA4) {
        // A4 SINGLE-PAGE: sticky footer anchored to page bottom.
        // items table filler Height is calculated based on PRECISE estimates 
        // to prevent overlap or page jumps.

        // Reuse pre-computed values from the dynamic fit check
        const topContentHeight = estTopH;
        let estimatedFooterHeight = estFooterH;
        const totalItemsHeight = estItemsH;

        // Row measurement (matching getResponsivePadding in builder)
        const visibleColsSp = templateConfig.itemTable?.columns?.filter((c: any) => c.show) || [];
        const colCountSp = visibleColsSp.length || 6;
        const cellPaddingSp = colCountSp >= 14 ? 1 : colCountSp >= 12 ? 2 : colCountSp >= 10 ? 2 : colCountSp >= 8 ? 3 : 4;
        const rowPaddingSp = cellPaddingSp * 2;
        const fontSizeSp = templateConfig.itemTable?.bodyStyle?.fontSize || 8;
        const baseRowHSp = templateConfig.itemTable?.bodyStyle?.rowHeight || 16;
        // Include a 1pt buffer for vertical lines / rounding
        const actualRowHSp = Math.max(baseRowHSp, (fontSizeSp * 1.2) + rowPaddingSp) + 1;

        const isDiscSplitSp = visibleColsSp.some((c: any) => c.key === 'discount' || c.key.includes('Disc')) &&
          visibleColsSp.some((c: any) => c.key === 'discountPercent' || c.key === 'discount%');
        const hdrRowsSp = isDiscSplitSp ? 2 : 1;
        const hdrEstSp = hdrRowsSp * actualRowHSp;

        // AGGRESSIVE FITTING: If total estimated height exceeds page area, 
        // scale down the footer specifically to pull it back onto Page 1.
        let footerFontScale = 1.0;
        const footerSpaceNeeded = estimatedFooterHeight;
        const footerSpaceAvailable = pageContentArea - topContentHeight - totalItemsHeight - 10;

        if (footerSpaceAvailable < footerSpaceNeeded) {
          // Calculate scale needed to fit, with 0.6 as the lower bound
          footerFontScale = Math.max(0.6, footerSpaceAvailable / footerSpaceNeeded);
          estimatedFooterHeight = Math.round(estimatedFooterHeight * footerFontScale);
        }

        builder.setFooterFontScale(footerFontScale);

        const footerY = Math.max(
          margins.top + 50,
          A4_HEIGHT - margins.bottom - estimatedFooterHeight
        );

        // Filler: stretch items table bottom edge towards footerY.
        // SAFETY BUFFER: topContentHeight is an estimate. When footer is small, footerY is
        // close to the bottom — even a tiny header underestimate causes the items table to
        // overflow to the next page. We cap the filler so the items table always stays
        // within the page, regardless of footer size.
        // The absolutePosition footer (white background) covers any resulting gap.
        // const FILLER_SAFETY = 1.0; // pts 
        // const JUMP_SAFETY = 5; // Reduced JUMP_SAFETY as we are now aggressively scaling

        // const idealFiller = footerY - margins.top - topContentHeight - totalItemsHeight - FILLER_SAFETY;
        // // Hard cap: items table can never exceed the page content area
        // const maxFiller = pageContentArea - topContentHeight - totalItemsHeight - JUMP_SAFETY;
        const FILLER_SAFETY = 0; // Natural flow ensures perfect connection
        const JUMP_SAFETY = 20; // Increased to ensure footer never gets pushed to next page

        const idealFiller = footerY - margins.top - topContentHeight - totalItemsHeight - FILLER_SAFETY;
        // Hard cap: items table can never exceed the page content area minus footer and safety
        const maxFiller = pageContentArea - topContentHeight - totalItemsHeight - estimatedFooterHeight - JUMP_SAFETY;

        // The items table needs to stretch to fill the gap up to the footer.
        // We do not cap it at a fixed value (like 300) because that causes a visible gap 
        // in the vertical borders when there are few items on an A4 page. 
        const itemsFillerHeight = Math.max(0, Math.round(Math.min(idealFiller, Math.max(0, maxFiller))));

        const FOOTER_SECTION_IDS = ['itemsSummary', 'totals', 'hsnBreakup', 'amountInWords', 'termsAndConditions', 'footer'];

        // ═══ STEP 1: Build and push header sections + items table (normal flow) ═══
        for (const row of templateConfig.layoutSettings.rows) {
          const topIds = row.sections.filter(sid => !FOOTER_SECTION_IDS.includes(sid));
          if (topIds.length > 0) {
            const builtSections = topIds.map(sid => {
              if (sid === 'items') {
                // showSummary=false: T.Qty/Disc row is in the absolutePosition block
                // hideBottomBorder=true: filler bottom edge is hidden (summary draws on top)
                return builder.buildItemsTable(undefined, false, false, true, itemsFillerHeight);
              }
              return this.buildSection(sid, builder, templateConfig);
            }).filter(s => s !== null);

            if (builtSections.length === 0) continue;

            let rowContent: any;
            if (builtSections.length === 1) {
              rowContent = builtSections[0];
            } else if (topIds.includes('business') && topIds.includes('invoiceDetails')) {
              rowContent = {
                columns: [
                  { width: '60%', ...builtSections[0] },
                  { width: '40%', ...builtSections[1] }
                ],
                columnGap: templateConfig.layoutSettings.sectionSpacing
              };
            } else if (topIds.includes('party') && topIds.includes('invoiceDetails') && templateConfig.pageSettings.pageSize === 'THERMAL') {
              // THERMAL: no side-by-side — invoice details first, customer details below
              rowContent = {
                stack: [builder.buildInvoiceDetailsSection(), builder.buildPartySection()],
                margin: [0, 0, 0, 0]
              };
            } else if (topIds.includes('party') && topIds.includes('invoiceDetails')) {
              const partyContent = builder.buildPartySection(true);
              const invoiceContent = builder.buildInvoiceDetailsSection(true);

              const borderColor = templateConfig.themeSettings?.borderColor || '#000000';
              const borderWidth = templateConfig.partyDetails?.borderWidth || 0.5;

              rowContent = {
                table: {
                  widths: ['*', '*'],
                  body: [[
                    { stack: partyContent ? [partyContent] : [] },
                    { stack: invoiceContent ? [invoiceContent] : [] }
                  ]]
                },
                layout: {
                  hLineWidth: (i: number) => i === 0 ? borderWidth : 0,
                  vLineWidth: (i: number, node: any) => {
                    if (i === 0) return borderWidth;
                    if (i === node.table.widths.length) return borderWidth;
                    return borderWidth;
                  },
                  hLineColor: () => borderColor,
                  vLineColor: () => borderColor,
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0,
                },
                margin: [0, 0, 0, 0]
              };
            } else {
              rowContent = {
                columns: builtSections,
                columnGap: templateConfig.layoutSettings.sectionSpacing
              };
            }
            if (rowContent) content.push(rowContent);
          }
        }

        // ═══ STEP 2: Build and push absolutePosition footer AFTER items table ═══
        // Pushed AFTER so pdfmake draws it ON TOP of items table — covers any
        // stray vertical lines that extend past footerY. The maxFiller cap ensures
        // items table never overflows the page, so this element stays on page 1.
        const footerSections: any[] = [];
        const processedSids = new Set<string>();
        for (const row of templateConfig.layoutSettings.rows) {
          for (const sid of row.sections) {
            const targetSid = sid === 'items' ? 'itemsSummary' : sid;
            if (FOOTER_SECTION_IDS.includes(targetSid) && !processedSids.has(targetSid)) {
              processedSids.add(targetSid);
              let section: any = null;
              if (targetSid === 'itemsSummary') {
                section = builder.buildItemsSummaryRow();
              } else {
                section = this.buildSection(targetSid, builder, templateConfig);
              }
              if (section) footerSections.push(section);
            }
          }
        }

        if (footerSections.length > 0) {
          content.push({
            stack: footerSections,
            margin: [0, 0, 0, 0]
          });
        }
      }
      else if (
        templateConfig.pageSettings.pageSize === 'A5' &&
        templateConfig.pageSettings.orientation !== 'landscape'
      ) {
        // ═══ A5 PORTRAIT: paginated items + sticky footer ═══
        const ITEMS_PER_PAGE = 8;
        const allItems = data.items || [];
        const totalPages = Math.max(1, Math.ceil(allItems.length / ITEMS_PER_PAGE));
        // FIX 1: Correct A5 portrait height (was 530.28 — wrong)
        const A5P_HEIGHT = 570.28;
        const margins = templateConfig.pageSettings.margins;
        const contentWidth = 419.53 - margins.left - margins.right;

        const a5pFooterEstimate = this.estimateA5PortraitFooterHeight(templateConfig, data);
        const a5pTopContentHeight = this.estimateA5PortraitTopContentHeight(templateConfig) + invoiceNameTitleHeight(templateConfig);
        const a5pPageContentArea = A5P_HEIGHT - margins.top - margins.bottom;

        // Row geometry
        const a5pVisibleCols = templateConfig.itemTable?.columns?.filter((c: any) => c.show) || [];
        const a5pColCount = a5pVisibleCols.length || 6;
        const a5pCellPadding = a5pColCount >= 10 ? 2 : a5pColCount >= 8 ? 3 : 4;
        const a5pRowPadding = a5pCellPadding * 2;
        const a5pFontSize = templateConfig.itemTable?.bodyStyle?.fontSize || 8;
        const a5pBaseRowH = templateConfig.itemTable?.bodyStyle?.rowHeight || 16;
        const a5pActualRowH = Math.max(a5pBaseRowH, (a5pFontSize * 1.2) + a5pRowPadding) + 1;
        const a5pHdrEst = a5pActualRowH; // 1 header row

        const a5pFooterY = A5P_HEIGHT - margins.bottom - a5pFooterEstimate;
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
          if (pageIndex > 0) content.push({ text: '', pageBreak: 'before' });

          // Invoice name repeats with the header on every A5 portrait page
          const a5pInvoiceName = buildInvoiceNameTitle(templateConfig);
          if (a5pInvoiceName) content.push(a5pInvoiceName);

          const isLastPage = pageIndex === totalPages - 1;
          const startIdx = pageIndex * ITEMS_PER_PAGE;
          const pageItems = allItems.slice(startIdx, startIdx + ITEMS_PER_PAGE);
          // ── Merge party+invoiceDetails into one row (idempotent) ──────────────
          const rows = templateConfig.layoutSettings.rows;
          const partyRowIdx = rows.findIndex((r: any) =>
            r.sections.includes('party') && !r.sections.includes('invoiceDetails'));
          const invoiceRowIdx = rows.findIndex((r: any) =>
            r.sections.includes('invoiceDetails') && !r.sections.includes('party'));
          if (partyRowIdx !== -1 && invoiceRowIdx !== -1) {
            rows[partyRowIdx] = {
              ...rows[partyRowIdx],
              sections: ['party', 'invoiceDetails'],
            };
            rows.splice(invoiceRowIdx, 1);
          }
          // ── Render header sections (business + party/invoice) ─────────────────
          for (const row of templateConfig.layoutSettings.rows) {
            if (row.sections.includes('items')) continue;
            if (row.sections.some((s: string) =>
              ['totals', 'hsnBreakup', 'termsAndConditions', 'footer', 'amountInWords'].includes(s)
            )) continue;
            const rowContent = this.buildRow(row.sections, builder, templateConfig);
            if (rowContent) content.push(rowContent);
          }

          let a5pFillerHeight = 0;
          if (isLastPage) {
            // Fix: Flow the footer naturally after the items table!
            // By NOT using absolutePosition, the items table and footer will always
            // perfectly touch — no gaps, no overlaps, regardless of row rounding.
            // We just need a conservative filler to push the footer near the bottom
            // without overflowing the page boundary.
            const estimatedFooterH = a5pFooterEstimate;
            const safetyBuffer = 20; // 20pt safety to absorb any under-estimated top content

            const a5pItemsBodyH = pageItems.length * a5pActualRowH;
            const remainingSpace = a5pPageContentArea
              - a5pTopContentHeight
              - a5pHdrEst
              - a5pItemsBodyH
              - estimatedFooterH
              - safetyBuffer;

            a5pFillerHeight = Math.max(0, Math.round(remainingSpace));
          }

          // ── Render items table ────────────────────────────────────────────────
          const expandTableA5P = totalPages > 1 && !isLastPage;
          const itemsTable = builder.buildItemsTable(
            pageItems,
            false,          // showSummary: false — summary is inside the footer block
            expandTableA5P, // isExpanded: stretch rows on non-last pages
            isLastPage,     // hideBottomBorder: footer sits on top
            a5pFillerHeight // filler to connect table border to footer top
          );
          if (itemsTable) content.push(itemsTable);
          if (isLastPage) {
            const a5pFooter = builder.buildA5PortraitFooterSection();
            if (a5pFooter) {
              content.push(a5pFooter);
            }
          }
        }
      }

      // else if (
      //   templateConfig.pageSettings.pageSize === 'A5' &&
      //   templateConfig.pageSettings.orientation !== 'landscape'
      // ) {
      //   // ═══ A5 PORTRAIT: paginated items + sticky footer ═══
      //   const ITEMS_PER_PAGE = 8;
      //   const allItems = data.items || [];
      //   const totalPages = Math.max(1, Math.ceil(allItems.length / ITEMS_PER_PAGE));

      //   const A5P_HEIGHT = 595.28;
      //   const margins = templateConfig.pageSettings.margins;
      //   const contentWidth = 419.53 - margins.left - margins.right;

      //   const a5pFooterEstimate = this.estimateA5PortraitFooterHeight(templateConfig, data);
      //   const a5pTopContentHeight = this.estimateA5PortraitTopContentHeight(templateConfig);

      //   const a5pPageContentArea = A5P_HEIGHT - margins.top - margins.bottom;

      //   // Row geometry
      //   const a5pVisibleCols = templateConfig.itemTable?.columns?.filter((c: any) => c.show) || [];
      //   const a5pColCount = a5pVisibleCols.length || 6;
      //   const a5pCellPadding = a5pColCount >= 10 ? 2 : a5pColCount >= 8 ? 3 : 4;
      //   const a5pRowPadding = a5pCellPadding * 2;
      //   const a5pFontSize = templateConfig.itemTable?.bodyStyle?.fontSize || 8;
      //   const a5pBaseRowH = templateConfig.itemTable?.bodyStyle?.rowHeight || 16;
      //   const a5pActualRowH = Math.max(a5pBaseRowH, (a5pFontSize * 1.2) + a5pRowPadding) + 1;
      //   const a5pHdrEst = a5pActualRowH; // 1 header row

      //   for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
      //     if (pageIndex > 0) content.push({ text: '', pageBreak: 'before' });

      //     const isLastPage = pageIndex === totalPages - 1;
      //     const startIdx = pageIndex * ITEMS_PER_PAGE;
      //     const pageItems = allItems.slice(startIdx, startIdx + ITEMS_PER_PAGE);

      //     // ── Header sections (every page) ──────────────────────────────────
      //     // Force party+invoiceDetails into same row
      //     const rows = templateConfig.layoutSettings.rows;
      //     const partyRowIdx = rows.findIndex((r: any) =>
      //       r.sections.includes('party') && !r.sections.includes('invoiceDetails'));
      //     const invoiceRowIdx = rows.findIndex((r: any) =>
      //       r.sections.includes('invoiceDetails') && !r.sections.includes('party'));
      //     if (partyRowIdx !== -1 && invoiceRowIdx !== -1) {
      //       rows[partyRowIdx] = {
      //         ...rows[partyRowIdx],
      //         sections: ['party', 'invoiceDetails'],
      //       };
      //       rows.splice(invoiceRowIdx, 1);
      //     }

      //     for (const row of templateConfig.layoutSettings.rows) {
      //       if (row.sections.includes('items')) continue;
      //       if (row.sections.some((s: string) =>
      //         ['totals', 'hsnBreakup', 'termsAndConditions', 'footer', 'amountInWords'].includes(s)
      //       )) continue;
      //       const rowContent = this.buildRow(row.sections, builder, templateConfig);
      //       if (rowContent) content.push(rowContent);
      //     }

      //     // ── Items table ───────────────────────────────────────────────────
      //     let a5pFillerHeight = 0;
      //     // if (isLastPage) {
      //     //   const footerY = A5P_HEIGHT - margins.bottom - a5pFooterEstimate;
      //     //   const a5pItemsBodyH = pageItems.length * a5pActualRowH;
      //     //   const rawFiller = footerY - margins.top - a5pTopContentHeight - a5pHdrEst - a5pItemsBodyH - 1;
      //     //   const maxFiller = a5pPageContentArea - a5pTopContentHeight - a5pHdrEst - a5pItemsBodyH - 5;
      //     //   a5pFillerHeight = Math.max(0, Math.round(Math.min(rawFiller, Math.max(0, maxFiller))));
      //     // }
      //     // AFTER:
      //     if (isLastPage) {
      //       // footerY: distance from page TOP to where footer starts — always pinned to bottom
      //       const footerY = A5P_HEIGHT - margins.bottom - a5pFooterEstimate;

      //       const a5pItemsBodyH = pageItems.length * a5pActualRowH;

      //       // a5pTopContentHeight comes from estimateA5PortraitTopContentHeight()
      //       // which uses exact builder constants: business=80pt + party/invoice=84pt = 164pt
      //       // filler = space between last item row bottom and footer top
      //       const rawFiller =
      //         footerY           // footer top Y (from page top)
      //         - margins.top     // top margin
      //         - a5pTopContentHeight  // exact: 164pt (business 80 + party/invoice 84)
      //         - a5pHdrEst            // items table header row
      //         - a5pItemsBodyH        // all item rows
      //         - 1;                   // 1pt sub-pixel safety

      //       // Cap: items table must never overflow onto page 2
      //       const capFiller = a5pPageContentArea - a5pTopContentHeight - a5pHdrEst - a5pItemsBodyH - 2;

      //       a5pFillerHeight = Math.max(0, Math.round(Math.min(rawFiller, Math.max(0, capFiller))));
      //     }

      //     // const itemsTable = builder.buildItemsTable(
      //     //   pageItems, false, true, isLastPage, a5pFillerHeight
      //     // );
      //     const expandTableA5P = totalPages > 1 && !isLastPage;
      //     const itemsTable = builder.buildItemsTable(
      //       pageItems, false, expandTableA5P, isLastPage, a5pFillerHeight
      //     );
      //     if (itemsTable) content.push(itemsTable);

      //     // ── Sticky footer (last page only) ────────────────────────────────
      //     if (isLastPage) {
      //       const a5pFooter = builder.buildA5PortraitFooterSection();
      //       if (a5pFooter) {
      //         const footerY = A5P_HEIGHT - margins.bottom - a5pFooterEstimate;
      //         content.push({
      //           table: {
      //             widths: [contentWidth],
      //             body: [[{
      //               stack: [a5pFooter],
      //               margin: [0, 0, 0, 0],
      //               fillColor: '#FFFFFF',
      //             }]],
      //           },
      //           layout: 'noBorders',
      //           absolutePosition: { x: margins.left, y: footerY },
      //         });
      //       }
      //     }
      //   }
      // }
      else {
        // ORIGINAL rendering for non-A4 pages
        const isA5Landscape = templateConfig.pageSettings.pageSize === 'A5' && templateConfig.pageSettings.orientation === 'landscape';
        console.log('[DynamicPdfTemplate] Non-A4 path. pageSize:', templateConfig.pageSettings.pageSize, 'orientation:', templateConfig.pageSettings.orientation, 'isA5Landscape:', isA5Landscape);

        // COMMENTED OUT: A5 Landscape custom pagination logic causing empty PDF on process payment
        /*
        if (isA5Landscape) {
          const ITEMS_PER_PAGE = 6;
          const allItems = data.items || [];
          const totalPages = Math.max(1, Math.ceil(allItems.length / ITEMS_PER_PAGE));

          // A5 landscape page dimensions (A5 rotated: width=595.28, height=419.53)
          const A5L_HEIGHT = 440.53;
          const margins = templateConfig.pageSettings.margins;
          const contentWidth = 595.28 - margins.left - margins.right;

          // Estimate footer height for absolute positioning
          const a5FooterEstimate = this.estimateA5LandscapeFooterHeight(templateConfig, data);

          // Pre-compute A5 landscape top-content height for filler calculation
          const a5TopContentHeight = this.estimateTopContentHeight(templateConfig);
          const a5PageContentArea = A5L_HEIGHT - margins.top - margins.bottom;

          // Row geometry for A5 landscape items table (mirrors builder's getResponsivePadding)
          const a5VisibleCols = templateConfig.itemTable?.columns?.filter((c: any) => c.show) || [];
          const a5ColCount = a5VisibleCols.length || 6;
          const a5CellPadding = a5ColCount >= 14 ? 1 : a5ColCount >= 12 ? 2 : a5ColCount >= 10 ? 2 : a5ColCount >= 8 ? 3 : 4;
          const a5RowPadding = a5CellPadding * 2;
          const a5FontSize = templateConfig.itemTable?.bodyStyle?.fontSize || 8;
          const a5BaseRowH = templateConfig.itemTable?.bodyStyle?.rowHeight || 16;
          const a5ActualRowH = Math.max(a5BaseRowH, (a5FontSize * 1.2) + a5RowPadding) + 1;
          const a5IsDiscSplit = a5VisibleCols.some((c: any) => c.key === 'discount' || c.key.includes('Disc')) &&
            a5VisibleCols.some((c: any) => c.key === 'discountPercent' || c.key === 'discount%');
          const a5HdrRows = a5IsDiscSplit ? 2 : 1;
          const a5HdrEst = a5HdrRows * a5ActualRowH;

          for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
            // Add page break before pages after the first
            if (pageIndex > 0) {
              content.push({ text: '', pageBreak: 'before' });
            }

            // Build header sections (business, party+invoice) for every page
            let partyAndInvoiceDone = false;
            for (const row of templateConfig.layoutSettings.rows) {
              // Skip items row — handled separately below
              if (row.sections.includes('items')) continue;

              // Skip individual party or invoiceDetails rows — they'll be combined
              if (!partyAndInvoiceDone && (row.sections.includes('party') || row.sections.includes('invoiceDetails'))) {
                const hasPartyRow = templateConfig.layoutSettings.rows.some(r => r.sections.includes('party'));
                const hasInvoiceRow = templateConfig.layoutSettings.rows.some(r => r.sections.includes('invoiceDetails'));

                if (hasPartyRow && hasInvoiceRow) {
                  const combined = builder.buildA5LandscapePartyAndInvoiceSection();
                  if (combined) content.push(combined);
                  partyAndInvoiceDone = true;
                  continue;
                }
              }
              if (partyAndInvoiceDone && (row.sections.includes('party') || row.sections.includes('invoiceDetails'))) {
                continue;
              }
              const rowContent = this.buildRow(row.sections, builder, templateConfig);
              if (rowContent) content.push(rowContent);
            }

            // Build items table for current page chunk
            const startIdx = pageIndex * ITEMS_PER_PAGE;
            const pageItems = allItems.slice(startIdx, startIdx + ITEMS_PER_PAGE);
            const isLastPage = pageIndex === totalPages - 1;
            const isMultiPage = totalPages > 1;

            // Multi-page: expand row height to fill page space (including last page)
            const expandTable = isMultiPage;

            // For the last page (single or multi-page), compute filler height so the items
            // table's bottom edge extends down to the footer's top border via vertical lines.
            // This closes the visible gap between the items table and the pinned footer.
            let a5ItemsFillerHeight = 0;
            if (isLastPage) {
              const footerY = A5L_HEIGHT - margins.bottom - a5FooterEstimate;
              const pageItemsCount = pageItems.length;
              const a5ItemsBodyH = pageItemsCount * a5ActualRowH;
              const rawFiller = footerY - margins.top - a5TopContentHeight - a5HdrEst - a5ItemsBodyH - 1;
              const maxFiller = a5PageContentArea - a5TopContentHeight - a5HdrEst - a5ItemsBodyH - 5;
              a5ItemsFillerHeight = Math.max(0, Math.round(Math.min(rawFiller, Math.max(0, maxFiller))));
            }

            const itemsTable = builder.buildItemsTable(pageItems, false, expandTable, false, a5ItemsFillerHeight);
            if (itemsTable) content.push(itemsTable);

            // Footer only on last page, pinned to bottom using absolutePosition
            if (isLastPage) {
              const a5Footer = builder.buildA5LandscapeFooterSection();
              if (a5Footer) {
                const footerY = A5L_HEIGHT - margins.bottom - a5FooterEstimate;
                content.push({
                  table: {
                    widths: [contentWidth],
                    body: [[{ stack: [a5Footer], margin: [0, 0, 0, 0] }]]
                  },
                  layout: 'noBorders',
                  absolutePosition: { x: margins.left, y: footerY }
                });
              }
            }
          }
        } else
        */
        {
          for (const row of templateConfig.layoutSettings.rows) {
            const rowContent = this.buildRow(row.sections, builder, templateConfig);
            if (rowContent) content.push(rowContent);
          }
        }
      }

      // Invoice name: small centered title at the top of page 1 (A4 and other sizes).
      // A5 portrait adds it per page above; thermal shows it in the invoice details block.
      const isA5PortraitPage = templateConfig.pageSettings.pageSize === 'A5' && templateConfig.pageSettings.orientation !== 'landscape';
      if (templateConfig.pageSettings.pageSize !== 'THERMAL' && !isA5PortraitPage) {
        const invoiceNameTitle = buildInvoiceNameTitle(templateConfig);
        if (invoiceNameTitle) content.unshift(invoiceNameTitle);
      }

      const pageSize = this.convertPageSize(templateConfig.pageSettings.pageSize);
      const pageSettings = templateConfig.pageSettings;
      const themeSettings = templateConfig.themeSettings;

      const pdfDefinition: TDocumentDefinitions = {
        pageSize: pageSize,
        pageOrientation: templateConfig.pageSettings.orientation,

        pageBreakBefore: function (currentNode, followingNodesOnPage) {
          return false;
        },
        // pageMargins: [
        //   templateConfig.pageSettings.margins.left,
        //   templateConfig.pageSettings.margins.top,
        //   templateConfig.pageSettings.margins.right,
        //   templateConfig.pageSettings.margins.bottom
        // ],
        pageMargins: templateConfig.pageSettings.pageSize === 'THERMAL'
          ? [
            Math.max(templateConfig.pageSettings.margins.left, 18),   // min 15pt left
            templateConfig.pageSettings.margins.top,
            Math.max(templateConfig.pageSettings.margins.right, 15),  // min 15pt right
            templateConfig.pageSettings.margins.bottom,
          ]
          : [
            templateConfig.pageSettings.margins.left,
            templateConfig.pageSettings.margins.top,
            templateConfig.pageSettings.margins.right,
            templateConfig.pageSettings.margins.bottom,
          ],
        content: content,
        styles: this.buildStyles(templateConfig),
        defaultStyle: {
          font: templateConfig.themeSettings.fontFamily,
          fontSize: templateConfig.themeSettings.baseFontSize,
          color: templateConfig.themeSettings.textColor
        }
      };

      if (pageSettings.showBorder) {
        const borderColor = pageSettings.borderColor || themeSettings.borderColor || '#000000';
        const borderWidth = pageSettings.borderWidth || 1;
        const borderStyle = pageSettings.borderStyle || 'solid';

        const pageSizeMap: { [key: string]: { width: number; height: number } } = {
          'A4': { width: 595.28, height: 841.89 },
          'A5': { width: 419.53, height: 595.28 },
          'A3': { width: 841.89, height: 1190.55 },
          'LETTER': { width: 612, height: 1008 },
          'THERMAL': { width: 226.77, height: 841.89 }
        };

        const pageSizeObj = pageSizeMap[pageSettings.pageSize] || pageSizeMap['A4'];
        const pageWidth = pageSettings.orientation === 'landscape' ? pageSizeObj.height : pageSizeObj.width;
        const pageHeight = pageSettings.orientation === 'landscape' ? pageSizeObj.width : pageSizeObj.height;

        const borderOffset = borderWidth / 2;
        const x1 = pageSettings.margins.left - borderOffset;
        const y1 = pageSettings.margins.top - borderOffset;
        const x2 = pageWidth - pageSettings.margins.right + borderOffset;
        const y2 = pageHeight - pageSettings.margins.bottom + borderOffset;

        let dashPattern: number[] | undefined = undefined;
        if (borderStyle === 'dashed') {
          dashPattern = [3, 2];
        } else if (borderStyle === 'dotted') {
          dashPattern = [1, 1];
        }

        (pdfDefinition as any).background = function (currentPage: number, pageSize: any) {
          if (borderStyle === 'double') {
            const offset = borderWidth * 1.5;
            return [
              {
                canvas: [
                  {
                    type: 'rect',
                    x: x1,
                    y: y1,
                    w: x2 - x1,
                    h: y2 - y1,
                    lineWidth: borderWidth,
                    lineColor: borderColor,
                  },
                  {
                    type: 'rect',
                    x: x1 + offset,
                    y: y1 + offset,
                    w: (x2 - x1) - (offset * 2),
                    h: (y2 - y1) - (offset * 2),
                    lineWidth: borderWidth,
                    lineColor: borderColor,
                  }
                ]
              }
            ];
          } else {
            const rect: any = {
              type: 'rect',
              x: x1,
              y: y1,
              w: x2 - x1,
              h: y2 - y1,
              lineWidth: borderWidth,
              lineColor: borderColor,
            };

            if (dashPattern) {
              rect.dash = { length: dashPattern[0], space: dashPattern[1] };
            }

            return [
              {
                canvas: [rect]
              }
            ];
          }
        };
      }

      if (templateConfig.watermark?.enabled && templateConfig.watermark?.text) {
        (pdfDefinition as any).watermark = {
          text: templateConfig.watermark.text,
          color: templateConfig.watermark.color || '#CCCCCC',
          opacity: templateConfig.watermark.opacity || 0.3,
          bold: templateConfig.watermark.bold ?? true,
          fontSize: templateConfig.watermark.fontSize || 60,
          angle: templateConfig.watermark.angle ?? -45
        };
      }

      return pdfDefinition;

    } catch (error) {
      console.error('[DynamicPdfTemplate] Error generating PDF:', error);

      // Fallback to a simple error PDF
      return this.generateErrorPdf(error);
    }
  }


  /**
   * Estimate the rendered height (in pdfmake points) of all A4 footer sections
   * so we can anchor them to the bottom of the page using absolutePosition.
   * @param includeItemsSummary - true (default) for single-page where summary row
   *   is a separate footer element; false for multi-page where it is inside items table.
   * Intentionally generous so footer never overflows below the border.
   */
  private estimateA4FooterHeight(config: DynamicTemplateConfig, data: InvoiceData, includeItemsSummary = true): number {
    let h = 0;
    const totals: any = config.totalsSection || {};
    const footer: any = config.footer || {};
    const hsnCfg: any = config.hsnBreakup || {};
    const itemTable: any = config.itemTable || {};
    const baseFontSize = config.themeSettings.baseFontSize || 8;

    // Check actual visibility of footer-area sections via layout flags + sectionOrder
    const totalsVisible = this.isSectionVisible(config, 'totals', config.layoutSettings.showTotals);
    const termsVisible = this.isSectionVisible(config, 'termsAndConditions', config.layoutSettings.showTermsAndConditions);
    const footerVisible = this.isSectionVisible(config, 'footer', config.layoutSettings.showFooter);
    const hsnVisible = hsnCfg.show && data.hsnBreakup && data.hsnBreakup.length > 0;

    // --- Items SUMMARY ROW (T.Qty / Disc / Total) ---
    if (includeItemsSummary) {
      const summaryRowHeight = itemTable.bodyStyle?.rowHeight || 18;
      h += summaryRowHeight + 2; // summary row + minimal padding
    }

    // --- Totals + HSN unified table (only if totals section is visible) ---
    if (totalsVisible) {
      // HSN rows use fontSize 7, totals use baseFontSize (~8). Row height ~12pt for HSN, ~14pt for totals.
      if (hsnVisible) {
        const hsnRowCount = 2 + (data.hsnBreakup?.length || 0) + 1;

        let summaryRowCount = 0;
        if (totals.showSubtotal) summaryRowCount++;
        if (totals.showDiscount) {
          const hasItemDiscount = (data as any).itemDiscount !== undefined && (data as any).itemDiscount > 0;
          const hasOverallDiscount = (data as any).overallDiscount !== undefined && (data as any).overallDiscount > 0;
          if (hasItemDiscount && hasOverallDiscount) summaryRowCount += 3;
          else if (((data as any).discount || 0) > 0) summaryRowCount++;
        }
        const loyaltyAmt = Number((data as any).loyaltyAmount) || 0;
        const loyaltyPts = Number((data as any).loyaltyPoints) || 0;
        if (loyaltyAmt > 0) summaryRowCount++;
        if (loyaltyPts > 0) summaryRowCount++;
        if (totals.showTaxBreakup) {
          if (totals.showCGST && (data as any).cgst) summaryRowCount++;
          if (totals.showSGST && (data as any).sgst) summaryRowCount++;
          if (totals.showIGST && (data as any).igst) summaryRowCount++;
        }
        if (totals.showRoundOff && (data as any).roundOffAmount) summaryRowCount++;
        if (totals.showGrandTotal) summaryRowCount++;

        // HSN rows can be tall due to cell padding
        const unifiedRowCount = Math.max(hsnRowCount, summaryRowCount);
        h += unifiedRowCount * 16 + 10;
      } else {
        // Count actual summary rows
        let summaryRowCount = 0;
        if (totals.showSubtotal) summaryRowCount++;
        if (totals.showDiscount) {
          const hasItemDiscount = (data as any).itemDiscount !== undefined && (data as any).itemDiscount > 0;
          const hasOverallDiscount = (data as any).overallDiscount !== undefined && (data as any).overallDiscount > 0;
          if (hasItemDiscount && hasOverallDiscount) summaryRowCount += 3;
          else if (((data as any).discount || 0) > 0) summaryRowCount++;
        }
        const loyaltyAmt = Number((data as any).loyaltyAmount) || 0;
        const loyaltyPts = Number((data as any).loyaltyPoints) || 0;
        if (loyaltyAmt > 0) summaryRowCount++;
        if (loyaltyPts > 0) summaryRowCount++;
        if (totals.showTaxBreakup) {
          if (totals.showCGST && (data as any).cgst) summaryRowCount++;
          if (totals.showSGST && (data as any).sgst) summaryRowCount++;
          if (totals.showIGST && (data as any).igst) summaryRowCount++;
        }
        if (totals.showRoundOff && (data as any).roundOffAmount) summaryRowCount++;
        if (totals.showGrandTotal) summaryRowCount++;
        // Each totals row: baseFontSize * 1.2 + cell padding ≈ 14pt per row
        const totalsRowH = baseFontSize * 1.2 + 4;
        h += Math.max(1, summaryRowCount) * totalsRowH + 4;
      }

      // Amount in words: single line, fontSize baseFontSize, margin [0,5,0,0]
      if (totals.showAmountInWords) h += baseFontSize * 1.2 + 8;
    }

    // --- Terms and Conditions (only if section is visible) ---
    if (termsVisible && footer.showTermsAndConditions && footer.termsText) {
      const points = (footer.termsText as string).split('\n').filter((p: string) => p.trim()).length;
      // Title line + ordered list points
      const titleH = baseFontSize * 1.2 + 2;
      const pointH = (baseFontSize - 1) * 1.2 + 2;
      h += titleH + Math.max(1, points) * pointH + 6; // +6 for border padding
    }

    // --- Footer sections: Bank Details, Declaration, Signatures (only if footer visible) ---
    if (footerVisible) {
      if (footer.showBankDetails) {
        // Title + 5 lines of bank info (fontSize baseFontSize-1) + padding
        const bankTextH = baseFontSize * 1.2 + (baseFontSize - 1) * 1.2 * 5 + 10;
        h += Math.max(bankTextH, 65); // QR is 40pts + margin/padding
      }

      if (footer.showDeclaration && footer.declarationText) {
        const pts = (footer.declarationText as string).split('\n').filter((p: string) => p.trim()).length;
        const declTitleH = baseFontSize * 1.2;
        const declPointH = (baseFontSize - 1) * 1.2 + 2;
        h += declTitleH + Math.max(1, pts) * declPointH + 10; // +10 for border/margin
      }

      if (footer.showReceiverSignature || footer.showSignature) {
        // Signature table: 40pt empty gap + "For Business" (12pt) + Image (30pt) + label (12pt)
        h += 95;
      }
    }

    return h;
  }

  /**
   * Estimate the vertical height of the A5 Landscape footer section.
   * Used to calculate absolutePosition Y for pinning footer to page bottom.
   */
  private estimateA5LandscapeFooterHeight(config: DynamicTemplateConfig, data: InvoiceData): number {
    let h = 0;
    const totals: any = config.totalsSection || {};
    const footer: any = config.footer || {};
    const baseFontSize = config.themeSettings.baseFontSize || 8;
    const fontSize = baseFontSize - 2;

    // Summary row (Total, T.Qty, etc.)
    h += fontSize * 1.2 + 10; // row height + padding

    // Totals rows (Round Off, Grand Total)
    let totalsRows = 0;
    if (totals.showRoundOff && data.roundOffAmount) totalsRows++;
    if (totals.showGrandTotal) totalsRows++;
    if (totalsRows > 0) h += totalsRows * (fontSize + 6) + 4;

    // Payment info (Received Amount, Previous Balance, Current Balance)
    let paymentCells = 0;
    if ((totals as any).showReceivedAmount) paymentCells++;
    if ((totals as any).showPreviousBalance) paymentCells++;
    if (totals.showCurrentBalance) paymentCells++;
    if (paymentCells > 0) h += fontSize * 1.2 + 12;

    // Amount in words
    if (totals.showAmountInWords) h += (totals.fontSize || baseFontSize - 2) * 1.2 + 4;

    // Notes
    if ((footer as any).showNotes && (footer as any).notesText) h += fontSize * 1.2 + 8;

    // 3-section footer (Bank + Terms + Signature)
    const hasFooterContent = footer.showBankDetails || footer.showTermsAndConditions || footer.showSignature;
    if (hasFooterContent) {
      let maxSectionH = 0;

      if (footer.showBankDetails) {
        // Title + bank lines + QR
        const bankH = (fontSize - 1) * 1.2 * 5 + 15;
        maxSectionH = Math.max(maxSectionH, bankH);
      }
      if (footer.showTermsAndConditions) {
        const termsText = footer.termsText || data.termsAndConditions || data.terms || '';
        const points = termsText ? termsText.split('\n').filter((p: string) => p.trim()).length : 0;
        const termsH = (fontSize - 1) * 1.2 + (fontSize - 1) * 1.2 * Math.max(1, points) + 10;
        maxSectionH = Math.max(maxSectionH, termsH);
      }
      if (footer.showSignature) {
        const sigH = (fontSize - 1) * 1.2 + 25 + (fontSize - 1) * 1.2 + 10;
        maxSectionH = Math.max(maxSectionH, sigH);
      }
      h += maxSectionH + 8; // +8 for table borders/margin
    }

    return h;
  }

  /**
   * Check if a section is actually visible based on both layoutSettings flags
   * and sectionOrder visibility.
   */
  private isSectionVisible(config: DynamicTemplateConfig, sectionId: string, layoutFlag?: boolean): boolean {
    if (layoutFlag === false) return false;
    const sectionConfig = config.layoutSettings.sectionOrder?.find(s => s.id === sectionId);
    if (sectionConfig && sectionConfig.visible === false) return false;
    // Also check if the section exists in any layout row
    const inLayoutRows = config.layoutSettings.rows?.some(r => r.sections.includes(sectionId));
    if (config.layoutSettings.rows && config.layoutSettings.rows.length > 0 && !inLayoutRows) return false;
    return true;
  }

  /**
   * Estimate the vertical height (in pdfmake points) consumed by the header sections
   * that appear ABOVE the items table on a single-page A4 render.
   * Dynamically checks visibility so hidden sections contribute 0 height.
   *
   * IMPORTANT: Slightly under-estimating is safe (the absolutePosition footer covers
   * tiny overflows), but over-estimating causes a visible gap between items table and
   * the footer summary bar. So we use tight, config-aware values.
   */
  private estimateTopContentHeight(config: DynamicTemplateConfig): number {
    let h = 0;
    const bc = config.businessDetails;
    const spacing = config.layoutSettings.sectionSpacing || 5;
    const baseFontSize = config.themeSettings.baseFontSize || 8;

    // Actual pdfmake line height: fontSize * 1.2 (default lineHeight) + bottom margin per element
    // Most elements use margin [0,0,0,2] i.e. 2pt bottom gap
    const lineH = baseFontSize * 1.2 + 2; // ~11.6 for baseFontSize 8

    // ── Business / header section ─────────────────────────────────────────────
    const businessVisible = this.isSectionVisible(config, 'business', config.layoutSettings.showBusinessDetails) ||
      this.isSectionVisible(config, 'header', config.layoutSettings.showBusinessDetails);
    if (businessVisible) {
      let textHeight = 0;
      if (bc?.showBusinessName !== false) {
        // Business name uses businessNameFontSize (typically baseFontSize + 4)
        const nameFs = bc.businessNameFontSize || (baseFontSize + 4);
        textHeight += nameFs * 1.2 + 0; // margin [0,0,0,0]
      }
      if (bc?.showTagline) {
        textHeight += (baseFontSize - 1) * 1.2 + 2;
      }
      if (bc?.showAddress !== false) {
        // Address may wrap to 2-3 lines; assume 2 lines minimum for safety
        textHeight += (baseFontSize - 1) * 1.2 * 2.0 + 2;
      }
      if (bc?.showPhone !== false || bc?.showEmail !== false || bc?.showWebsite) {
        textHeight += (baseFontSize - 1) * 1.2 + 2; // columns layout = single row
      }
      if (bc?.showGSTIN !== false || bc?.showPan) {
        textHeight += (baseFontSize - 1) * 1.2 + 2;
      }
      // If wrapped in border table, pdfmake adds default cell padding (2pt top + 2pt bottom)
      // plus margin [5,1,5,0] inside the cell + 0.5pt border width
      if (bc?.showBorder) {
        textHeight += 6; // border table cell padding + margin
      }

      let logoHeight = 0;
      if (bc?.showLogo && bc?.logoWidth) {
        logoHeight = bc.logoWidth * 0.75 + 2; // typical aspect ratio + margin
      }
      h += Math.max(textHeight, logoHeight);
      h += spacing;
    }

    // ── Party + Invoice Details section (side-by-side) ────────────────────────
    const partyVisible = this.isSectionVisible(config, 'party', config.layoutSettings.showPartyDetails);
    const invoiceVisible = this.isSectionVisible(config, 'invoiceDetails', config.layoutSettings.showInvoiceDetails);

    if (partyVisible || invoiceVisible) {
      const pc = config.partyDetails;
      const ic = config.invoiceDetails;

      // Party height: count actual visible fields
      let partyHeight = 0;
      if (partyVisible && pc) {
        if (pc.showBillTo) {
          // Name line with prefix: fontSize baseFontSize, margin [0,0,0,3]
          partyHeight += baseFontSize * 1.2 + 3;
          // Address: fontSize baseFontSize-3, margin [0,0,0,1]
          if (pc.showCustomerAddress !== false) partyHeight += (baseFontSize - 3) * 1.2 + 1;
          // Phone: fontSize baseFontSize-2, margin [0,0,0,1]
          if (pc.showCustomerPhone !== false) partyHeight += (baseFontSize - 2) * 1.2 + 1;
          // GSTIN: fontSize baseFontSize-2, margin [0,0,0,1]
          if (pc.showCustomerGSTIN !== false) partyHeight += (baseFontSize - 2) * 1.2 + 1;
        }
        if (pc.showShipTo) {
          // Similar to BillTo; when both shown, they're side-by-side so don't double height
          if (!pc.showBillTo) {
            partyHeight += baseFontSize * 1.2 + 3 + (baseFontSize - 3) * 1.2 * 2.0 + 1 + (baseFontSize - 2) * 1.2 + 1;
          }
        }
        if (pc.showDispatchFrom && !pc.showBillTo && !pc.showShipTo) {
          partyHeight += baseFontSize * 1.2 + 3 + (baseFontSize - 3) * 1.2 + 1;
        }

        // Border table adds default 2+2 padding
        if (pc.showBorder) partyHeight += 5;
      }

      // Invoice details height (inline stacked mode): label-value table rows
      let invoiceHeight = 0;
      if (invoiceVisible && ic) {
        let iRows = 0;
        if (ic.showInvoiceNumber) iRows += 1;
        if (ic.showInvoiceDate !== false) iRows += 1;
        if (ic.showTime) iRows += 1;
        if (ic.showModeOfPayment) iRows += 1;
        if (ic.showReferenceNo) iRows += 1;
        if (ic.showSODCNo) iRows += 1;
        if (ic.showEwayBillNumber) iRows += 1;
        if (ic.showVehicleNumber && !ic.showEwayBillNumber) iRows += 1;

        // Each row height: ~15pts with padding and borders
        const invoiceRowH = (baseFontSize - 1) * 1.2 + 4;
        invoiceHeight = iRows * invoiceRowH;
        if (ic.showBorder) invoiceHeight += 6;
      }

      // Wrapper table with zero padding: height = max of both columns
      h += Math.max(partyHeight, invoiceHeight);
      h += spacing;
    }
    // No else clause — if both are hidden, add 0 height

    return h;
  }

  /**
   * Estimate total vertical height of the items table (headers + all rows).
   * Accurate estimation of description wrapping prevents footer overlap.
   */
  private estimateA4ItemsHeight(config: DynamicTemplateConfig, items: any[]): number {
    const tableConfig = config.itemTable;
    const visibleCols = tableConfig.columns?.filter((c: any) => c.show) || [];
    const colCount = visibleCols.length || 6;

    const cellPadding = colCount >= 14 ? 1 : colCount >= 12 ? 2 : colCount >= 10 ? 2 : colCount >= 8 ? 3 : 4;
    const rowPadding = cellPadding * 2;
    const fontSize = tableConfig.bodyStyle?.fontSize || 8;
    const baseRowH = tableConfig.bodyStyle?.rowHeight || 16;
    const actualRowH = Math.max(baseRowH, (fontSize * 1.2) + rowPadding);

    // Header logic (discount split adds a row)
    const isDiscSplit = visibleCols.some((c: any) => c.key === 'discount' || c.key.includes('Disc')) &&
      visibleCols.some((c: any) => c.key === 'discountPercent' || c.key === 'discount%');
    const hdrRows = isDiscSplit ? 2 : 1;
    let totalH = hdrRows * actualRowH;

    for (const item of items) {
      let lines = 1;
      const name = item.name || '';
      const desc = tableConfig.showItemDescription ? (item.description || '') : '';

      // Count manual newlines
      const nameNewlines = (name.match(/\n/g) || []).length + 1;
      const descNewlines = desc ? (desc.match(/\n/g) || []).length + 1 : 0;

      // Rough width estimate for wrapping (Assuming desc/name column is 150-250pts)
      const charsPerLine = colCount > 10 ? 25 : 40;
      const nameWrapped = Math.ceil(name.length / charsPerLine);
      const descWrapped = desc ? Math.ceil(desc.length / charsPerLine) : 0;

      lines = Math.max(nameNewlines, nameWrapped) + Math.max(descNewlines, descWrapped);
      // Added a 2pt buffer per row to account for line height variations
      totalH += Math.max(actualRowH, lines * (fontSize * 1.1) + rowPadding) + 2;
    }

    return totalH;
  }

  private convertPageSize(pageSize: 'A3' | 'A4' | 'A5' | 'LETTER' | 'THERMAL'): any {
    if (pageSize === 'THERMAL') {
      return { width: 226.77, height: 847 }; // 80mm thermal paper
    }
    return pageSize;
  }


  private buildRow(sections: string[], builder: DynamicSectionBuilder, config: DynamicTemplateConfig): any {
    if (sections.length === 0) return null;

    const builtSections = sections
      .map(sectionId => this.buildSection(sectionId, builder, config))
      .filter(section => section !== null);

    if (builtSections.length === 0) return null;

    if (builtSections.length === 1) {
      return builtSections[0];
    }

    if (sections.includes('business') && sections.includes('invoiceDetails')) {
      return {
        columns: [
          { width: '60%', ...builtSections[0] },
          { width: '40%', ...builtSections[1] }
        ],
        columnGap: config.layoutSettings.sectionSpacing
      };
    }

    // When 'party' (Bill To / Ship To) and 'invoiceDetails' are side by side in the
    // same layout row, wrap them in a SINGLE outer table so that:
    //   1. They share a continuous top border line and outer bounding box
    //   2. There is zero gap between them (the middle divider is shared)
    //   3. margin=[0,0,0,0] so items table header connects directly below (no double-border)
    // THERMAL: no side-by-side — invoice details first, customer details below
    if (sections.includes('party') && sections.includes('invoiceDetails') && config.pageSettings.pageSize === 'THERMAL') {
      return {
        stack: [builder.buildInvoiceDetailsSection(), builder.buildPartySection()],
        margin: [0, 0, 0, 0]
      };
    }

    if (sections.includes('party') && sections.includes('invoiceDetails')) {
      const pageSettings = config.pageSettings;
      // A5 Landscape: use custom combined layout (COMMENTED OUT)
      // if (pageSettings.pageSize === 'A5' && pageSettings.orientation === 'landscape') {
      //   return builder.buildA5LandscapePartyAndInvoiceSection();
      // }

      const partyContent = builder.buildPartySection(true);
      const invoiceContent = builder.buildInvoiceDetailsSection(true);

      const borderColor = config.themeSettings?.borderColor || '#000000';
      const borderWidth = 0.3;

      // ═══ A5 PORTRAIT: Invoice left (40%), Party right (60%) ═══
      if (pageSettings.pageSize === 'A5' && pageSettings.orientation !== 'landscape') {
        return {
          table: {
            widths: ['40%', '60%'],   // invoice narrower left, party wider right
            body: [[
              { stack: partyContent ? [partyContent] : [], margin: [0, 0, 0, 0] },
              { stack: invoiceContent ? [invoiceContent] : [], margin: [0, 0, 0, 0] },
            ]]
          },
          layout: {
            hLineWidth: (i: any, node: any) => {
              // Remove bottom border
              if (i === node.table.body.length) {
                return 0;
              }
              return borderWidth;
            },
            vLineWidth: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
          },
          margin: [0, 0, 0, 0],
        };
      }
      return {
        table: {
          widths: ['60%', '*'],
          body: [[
            { stack: partyContent ? [partyContent] : [] },
            { stack: invoiceContent ? [invoiceContent] : [] }
          ]]
        },
        layout: {
          // Draw top border + outer left/right borders + middle divider
          hLineWidth: (i: number) => i === 0 ? borderWidth : 0.5,  // top border only
          vLineWidth: (i: number, node: any) => {
            if (i === 0) return borderWidth; // left outer edge
            if (i === node.table.widths.length) return borderWidth; // right outer edge
            return borderWidth; // middle divider
          },
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0]  // no bottom gap: items table header connects directly
      };
    }

    return {
      columns: builtSections,
      columnGap: config.layoutSettings.sectionSpacing
    };
  }


  private buildSection(sectionId: string, builder: DynamicSectionBuilder, config: DynamicTemplateConfig): any {
    const sectionConfig = config.layoutSettings.sectionOrder.find(s => s.id === sectionId);
    if (sectionConfig && !sectionConfig.visible) {
      return null;
    }

    // A5 Landscape: hide all footer sections (totals through authorized signature) - COMMENTED OUT
    // const isA5Landscape = config.pageSettings.pageSize === 'A5' && config.pageSettings.orientation === 'landscape';
    // if (isA5Landscape && ['totals', 'hsnBreakup', 'termsAndConditions', 'footer', 'amountInWords'].includes(sectionId)) {
    //   return null;
    // }

    const layoutSettings = config.layoutSettings;

    switch (sectionId) {
      case 'business':
      case 'header':
        if (layoutSettings.showBusinessDetails === false) {
          return null;
        }
        return builder.buildBusinessSection();

      case 'invoiceDetails':
        if (layoutSettings.showInvoiceDetails === false) {
          return null;
        }
        return builder.buildInvoiceDetailsSection();

      case 'party':
        if (layoutSettings.showPartyDetails === false) {
          return null;
        }
        return builder.buildPartySection();

      case 'items':
        if (layoutSettings.showItemTable === false) {
          return null;
        }
        return builder.buildItemsTable();

      case 'totals':
        if (layoutSettings.showTotals === false) {
          return null;
        }
        return builder.buildTotalsSection();

      case 'termsAndConditions':
        if (layoutSettings.showTermsAndConditions === false) {
          return null;
        }
        return config.pageSettings.pageSize === 'THERMAL' && !config.layoutSettings.showFooter ? builder.buildFooterSection() : builder.buildTermsAndConditionsSection();

      case 'footer':
        if (layoutSettings.showFooter === false) {
          return null;
        }
        return builder.buildFooterSection();

      case 'hsnBreakup':
        if (!config.hsnBreakup || !config.hsnBreakup.show) {
          return null;
        }
        return builder.buildHsnBreakupSection();

      case 'amountInWords':
        return null;

      default:
        console.warn(`[DynamicPdfTemplate] Unknown section: ${sectionId}`);
        return null;
    }
  }

  private buildPaginatedRow(sections: string[], builder: DynamicSectionBuilder, config: DynamicTemplateConfig, items: any[], isLastPage: boolean): any {
    if (sections.length === 0) return null;

    const builtSections = sections
      .map(sectionId => this.buildPaginatedSection(sectionId, builder, config, items, isLastPage))
      .filter(section => section !== null);

    if (builtSections.length === 0) return null;

    if (builtSections.length === 1) {
      return builtSections[0];
    }

    const sectionSpacing = config.layoutSettings.sectionSpacing || 5;
    const currentColumnGap = isLastPage ? sectionSpacing : sectionSpacing * 2;
    const currentMargin: [number, number, number, number] = [0, 0, 0, isLastPage ? 5 : 10];

    if (sections.includes('business') && sections.includes('invoiceDetails')) {
      return {
        columns: [
          { width: '60%', ...builtSections[0] },
          { width: '40%', ...builtSections[1] }
        ],
        columnGap: currentColumnGap,
        margin: currentMargin
      };
    }

    // THERMAL: no side-by-side — invoice details first, customer details below
    if (sections.includes('party') && sections.includes('invoiceDetails') && config.pageSettings.pageSize === 'THERMAL') {
      return {
        stack: [builder.buildInvoiceDetailsSection(), builder.buildPartySection()],
        margin: currentMargin
      };
    }

    if (sections.includes('party') && sections.includes('invoiceDetails')) {
      const partyContent = builder.buildPartySection(true);
      const invoiceContent = builder.buildInvoiceDetailsSection(true);

      const borderColor = config.themeSettings?.borderColor || '#000000';
      const borderWidth = config.partyDetails?.borderWidth || 0.5;

      return {
        table: {
          widths: ['60%', '*'],
          body: [[
            { stack: partyContent ? [partyContent] : [] },
            { stack: invoiceContent ? [invoiceContent] : [] }
          ]]
        },
        layout: {
          // Draw top border + outer left/right borders + middle divider
          hLineWidth: (i: number) => i === 0 ? borderWidth : 0,  // top border only
          vLineWidth: (i: number, node: any) => {
            if (i === 0) return borderWidth; // left outer edge
            if (i === node.table.widths.length) return borderWidth; // right outer edge
            return borderWidth; // middle divider
          },
          hLineColor: () => borderColor,
          vLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0]  // no bottom gap: items table header connects directly
      };
    }

    return {
      columns: builtSections,
      columnGap: currentColumnGap,
      margin: currentMargin
    };
  }

  private buildPaginatedSection(sectionId: string, builder: DynamicSectionBuilder, config: DynamicTemplateConfig, items: any[], isLastPage: boolean): any {
    const sectionConfig = config.layoutSettings.sectionOrder.find(s => s.id === sectionId);
    if (sectionConfig && !sectionConfig.visible) {
      return null;
    }

    const layoutSettings = config.layoutSettings;

    switch (sectionId) {
      case 'items':
        if (layoutSettings.showItemTable === false) {
          return null;
        }
        // Use chunk items, show summary ONLY on last page, and expanded mode ONLY if NOT last page
        return builder.buildItemsTable(items, isLastPage, !isLastPage);

      case 'business':
      case 'header':
      case 'invoiceDetails':
      case 'party':
      case 'totals':
      case 'termsAndConditions':
      case 'footer':
      case 'hsnBreakup':
      case 'amountInWords':
        return this.buildSection(sectionId, builder, config);

      default:
        return null;
    }
  }

  /**
   * Build PDF styles from theme configuration
   */
  private buildStyles(config: DynamicTemplateConfig): any {
    return {
      tableHeader: {
        bold: true,
        fontSize: config.itemTable.headerStyle.fontSize,
        color: config.itemTable.headerStyle.textColor,
        fillColor: config.itemTable.headerStyle.backgroundColor
      },
      header: {
        fontSize: config.themeSettings.baseFontSize + 4,
        bold: true,
        color: config.themeSettings.primaryColor
      },
      subheader: {
        fontSize: config.themeSettings.baseFontSize + 2,
        bold: true,
        color: config.themeSettings.secondaryColor
      },
      small: {
        fontSize: config.themeSettings.baseFontSize - 2,
        color: config.themeSettings.secondaryColor
      }
    };
  }

  /**
   * Generate error PDF when configuration fails
   */
  private generateErrorPdf(error: any): TDocumentDefinitions {
    console.error('[DynamicPdfTemplate] Generating error PDF due to:', error);

    return {
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],

      content: [
        {
          text: 'PDF Generation Error',
          fontSize: 20,
          bold: true,
          color: '#FF0000',
          margin: [0, 0, 0, 20]
        },
        {
          text: 'Failed to generate PDF using dynamic template configuration.',
          fontSize: 12,
          margin: [0, 0, 0, 10]
        },
        {
          text: `Error: ${error?.message || 'Unknown error'}`,
          fontSize: 10,
          color: '#666666',
          margin: [0, 0, 0, 10]
        },
        {
          text: 'Please contact support or try using a different template.',
          fontSize: 10,
          italics: true,
          color: '#999999'
        }
      ]
    };
  }
}